import { uuidv4 } from '@/utils'

function getDefaultCollection() {
  return {
    name: `Collection`,
    isUriMode: false,
    fileFilter: {
      input: null,
      isRegex: null,
    },
    isRawData: false,
    assignSamples: false,
    savedDimensionsValues: [null],
    dimensionsValues: [null],
    dimensionsId: null,
    studies: null,
  }
}

function getDefaultState() {
  return {
    collections: {},
    selectedCollectionId: '',
    previouslySelectedCollectionId: '',
    collectionErrors: {},
  }
}

const state = {
  ...getDefaultState(),
}

const getters = {
  newCollection: (state, getters) => (collectionData) => {
    const name =
      (collectionData?.name || 'Collection') +
      ` #${getters.collectionIds.length + 1}`
    return {
      ...getDefaultCollection(),
      id: `c${uuidv4()}`,
      ...collectionData,
      name,
    }
  },
  collectionIds: (state) => {
    return Object.keys(state.collections)
  },
  collectionCount: (_, getters) => {
    return getters.collectionIds.length
  },
  collectionList: (state, getters) => {
    return getters.collectionIds.map((id) => ({
      id,
      name: state.collections[id].name,
      errors: state.collectionErrors[id] || {},
      datasetsList: getters.getCollectionDatasetsList(id),
      isUriMode: state.collections[id]?.isUriMode === true,
      isRawData: state.collections[id]?.isRawData === true,
      assignSamples: state.collections[id]?.assignSamples === true,
      fileFilter: state.collections[id]?.fileFilter,
      dimensionsValues: state.collections[id]?.dimensionsValues || [],
      dimensionsId: state.collections[id]?.dimensionsId,
      savedDimensionsValues: state.collections[id]?.savedDimensionsValues || [],
      studies: state.collections[id]?.studies,
    }))
  },
  collectionsIdsWithStagedDatasets: (state, getters) => {
    return getters.collectionIds.filter((id) => {
      return getters.datasetsListsByCollectionId[id]?.length > 0
    })
  },
  hasRawDataCollection: (state, getters) => {
    let hasRawDataCollection = false
    for (const id of getters.collectionIds) {
      const collection = state.collections[id]
      const collectionIsRaw = collection.isRawData === true
      if (collectionIsRaw) {
        hasRawDataCollection = true
        break
      }
    }
    return hasRawDataCollection
  },
  hasAssignSamplesCollection: (state, getters) => {
    let assignSamples = false
    for (const id of getters.collectionIds) {
      const collection = state.collections[id]
      const collectionIsRaw = collection.isRawData === true
      const collectionAssignSamples = collection.assignSamples === true
      if (collectionIsRaw || collectionAssignSamples) {
        assignSamples = true
        break
      }
    }
    return assignSamples
  },

  // ------------------------------------------------------------------
  datasetsListsByCollectionId: (state, getters, rootState, rootGetters) => {
    const datasetsList = rootGetters['dataloader/datasets/datasetsList']
    return datasetsList.reduce((datasetsListsByCollectionId, dataset) => {
      const collectionId = dataset.collectionId
      const collectionDatasets = datasetsListsByCollectionId[collectionId] || []
      datasetsListsByCollectionId[collectionId] = [
        ...collectionDatasets,
        dataset,
      ]
      return datasetsListsByCollectionId
    }, {})
  },
  getCollectionDatasetsList: (state, getters) => (id) => {
    return getters.datasetsListsByCollectionId[id] || []
  },
  // getCollectionById: (state, getters) => (id) => {
  getCollectionById: (state, getters, rootState, rootGetters) => (id) => {
    const filesById = rootGetters['dataloader/dropbox/selectedFiles']
    // const filesById = getters.files
    const collectionDatasetsList = getters.getCollectionDatasetsList(id)
    return {
      ...state.collections[id],
      datasetsList: collectionDatasetsList.map((d) => {
        return {
          ...d,
          files: d.fileIds.map((fId) => filesById[fId]),
        }
      }),
    }
  },
  getCollectionDatasetIdsList: (state, getters) => (id) => {
    return getters.getCollectionDatasetsList(id).map((d) => d.id)
  },
  getCollectionIsRawData: (state) => (id) => {
    return state.collections[id]?.isRawData === true
  },
  getCollectionIsUriMode: (state) => (id) => {
    return state.collections[id]?.isUriMode === true
  },
  getCollectionDimensions: (state) => (id) => {
    return state.collections[id]?.dimensionsValues || []
  },
  getCollectionFileFilter: (state) => (id) => {
    return state.collections[id]?.fileFilter
  },
}

const actions = {
  async reset({ commit }) {
    commit('RESET', getDefaultState())
  },

  addCollection({ commit, getters }, collectionData) {
    const collection = getters.newCollection(collectionData)
    commit('ADD_COLLECTION', { id: collection.id, collection })
  },
  editCollection({ commit }, { id, editData }) {
    commit('EDIT_COLLECTION_BY_ID', { id, editData })
  },
  removeCollection({ state, commit, dispatch, getters }, id) {
    dispatch('removeCollectionDatasets', id)
    commit('REMOVE_COLLECTION', id)
    // reselect another collection
    const selectedBeforeAndExists =
      getters.collectionIds.includes(state.previouslySelectedCollectionId) &&
      state.previouslySelectedCollectionId
    commit(
      'SELECT_COLLECTION_BY_ID',
      selectedBeforeAndExists || getters.collectionIds[0] || '',
    )
  },
  removeCollectionDatasets({ commit, getters }, collectionId) {
    const ids = getters.getCollectionDatasetIdsList(collectionId)
    commit('dataloader/datasets/REMOVE_DATASETS_BY_IDS', ids, { root: true })
  },
  setDimensionsId({ commit }, payload) {
    commit('SET_DIMENSIONS_ID', payload)
  },
  setIsRaw({ commit }, payload) {
    const { collectionId, isRawData } = payload
    commit('SET_IS_RAW', { collectionId, isRawData })
  },
  setIsUriMode({ commit }, payload) {
    commit('SET_IS_URI_MODE', payload)
  },
  setFileFilterInput({ commit, getters }, { collectionId, input }) {
    const filt = getters.getCollectionFileFilter(collectionId)
    commit('SET_FILE_FILTER', {
      collectionId,
      fileFilter: { ...filt, input },
    })
  },
  setFileFilterIsRegex({ commit, getters }, { collectionId, isRegex }) {
    const filt = getters.getCollectionFileFilter(collectionId)
    commit('SET_FILE_FILTER', {
      collectionId,
      fileFilter: { ...filt, isRegex },
    })
  },
  spliceDimensions({ commit, getters }, { collectionId, endIndex }) {
    const dimensions = getters.getCollectionDimensions(collectionId)
    const dimLength = dimensions.length
    const spliceLength = dimLength - endIndex
    commit('SPLICE_COLLECTION_DIMENSIONS', {
      collectionId,
      endIndex,
      spliceLength,
    })
  },
  async wipeCollections({ commit }) {
    commit('dataloader/datasets/REMOVE_ALL_DATASETS', null, { root: true })
    commit('WIPE_COLLECTIONS')
  },
}

const mutations = {
  RESET(state, defaultState) {
    Object.assign(state, { ...defaultState })
  },
  SELECT_COLLECTION_BY_ID(state, id) {
    state.previouslySelectedCollectionId = state.selectedCollectionId
    state.selectedCollectionId = id
  },
  SET_ERROR(state, { id, error }) {
    state.collectionErrors[id] = error
  },
  ADD_COLLECTION(state, { id, collection }) {
    state.collections[id] = collection
    for (const [key, value] of Object.entries(collection)) {
      state.collections[id][key] = value
    }
    const isRawData = state.collections[id].isRawData === true
    state.collections[id].assignSamples = isRawData
    state.selectedCollectionId = id
  },
  REMOVE_COLLECTION(state, id) {
    delete state.collections[id]
    delete state.collectionsErrors[id]
  },
  WIPE_COLLECTIONS(state) {
    state.collections = {}
    state.collectionErrors = {}
  },
  EDIT_COLLECTION_BY_ID(state, { id, editData }) {
    state.collections[id] = {
      ...state.collections[id],
      ...editData,
    }
  },
  SET_IS_RAW(state, { collectionId, isRawData }) {
    state.collections[collectionId].isRawData = isRawData
    state.collections[collectionId].assignSamples = isRawData
  },
  SET_ASSIGN_SAMPLES(state, { collectionId, assignSamples }) {
    state.collections[collectionId].assignSamples = assignSamples
  },
  SET_IS_URI_MODE(state, { collectionId, isUriMode }) {
    state.collections[collectionId].isUriMode = isUriMode
  },
  SET_DIMENSIONS_ID(state, { collectionId, dimensionsId }) {
    state.collections[collectionId].dimensionsId = dimensionsId
  },
  SET_FILE_FILTER(state, { collectionId, fileFilter }) {
    state.collections[collectionId].fileFilter = fileFilter
  },
  SET_DIMENSION_VALUE(state, { collectionId, index, dimensionValue }) {
    state.collections[collectionId].dimensionsValues[index] = dimensionValue
    state.collections[collectionId].savedDimensionsValues[index] =
      dimensionValue
  },
  SPLICE_COLLECTION_DIMENSIONS(
    state,
    { collectionId, endIndex, spliceLength },
  ) {
    state.collections[collectionId].dimensionsValues.splice(
      endIndex,
      spliceLength,
    )
  },
}

const dataloader = {
  namespaced: true,
  state,
  actions,
  getters,
  mutations,
}

export default dataloader
